import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2, AlertCircle, Loader2, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import axios from 'axios';
import AuthShell from './AuthShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invite token found');
      setVerifying(false);
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/retreat/invites/verify/${token}`);
        setInviteData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invite');
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/invites/accept`, {
        token,
        password,
      });
      localStorage.setItem('attendee_token', res.data.token);
      localStorage.setItem('attendee_id', res.data.attendee_id || '');
      setSuccess(true);
      setTimeout(() => {
        if (res.data.attendee_id) {
          navigate(`/portal/${res.data.attendee_id}`);
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <AuthShell eyebrow="Attendee Portal" title="Verifying your invite...">
        <div
          className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-10 text-center"
          data-testid="invite-verifying"
        >
          <Loader2 className="w-8 h-8 animate-spin text-white/40 mx-auto" />
          <p className="text-sm text-white/60 mt-4">One moment please.</p>
        </div>
      </AuthShell>
    );
  }

  if (!inviteData && error) {
    return (
      <AuthShell eyebrow="Attendee Portal" title="Invite issue">
        <div
          className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-10 text-center"
          data-testid="invite-error"
        >
          <AlertCircle className="w-10 h-10 text-red-300 mx-auto mb-4" />
          <p className="text-lg font-display text-white/90 tracking-tight">{error}</p>
          <p className="text-sm text-white/55 mt-3 leading-relaxed">
            Contact the retreat organizers for a new invite.
          </p>
          <Link
            to="/"
            className="text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors mt-5 inline-block"
          >
            Back to site
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell eyebrow="Attendee Portal" title="Account created">
        <div
          className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-10 text-center"
          data-testid="invite-success"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-4" />
          <p className="text-lg font-display text-white/90 tracking-tight">You're all set</p>
          <p className="text-sm text-white/55 mt-3">Redirecting to your retreat portal...</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Attendee Portal"
      title="Set up your account"
      subtitle={inviteData?.attendee_name ? `Welcome, ${inviteData.attendee_name}` : undefined}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-7"
        data-testid="accept-invite-form"
      >
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-3.5 h-3.5 text-white/40" />
          <p className="eyebrow text-white/40">Create Password</p>
        </div>

        <div className="mb-5 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10">
          <p className="eyebrow text-white/40 mb-1">Email</p>
          <p className="text-sm font-medium text-white/85 break-all">{inviteData?.email}</p>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-4 h-4" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="pl-10 pr-10 bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
              data-testid="invite-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
              aria-label="Toggle password visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-4 h-4" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="pl-10 bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
              data-testid="invite-confirm-password"
            />
          </div>

          {error && (
            <p className="text-xs text-red-300 pl-1" data-testid="invite-error-msg">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-base btn-lift group"
            data-testid="invite-submit-btn"
          >
            {submitting ? 'Creating Account...' : 'Create Account'}
            {!submitting && (
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            )}
          </Button>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-white/80 hover:text-white underline underline-offset-4 transition-colors"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default AcceptInvite;
